
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ExternalLink } from "lucide-react";

const About = () => {
  const founder = {
    name: "George Woodworth",
    role: "Founder",
    bio: "George created Upfixe Ads with a vision to transform how businesses approach Google Ads. An automation genius with a passion for transforming complex problems into elegant solutions. Check out his YouTube channel for more tech insights.",
    avatar: "/placeholder.svg",
    initials: "GW",
    linkedin: "https://www.linkedin.com/in/georgewoodworth",
    youtube: "https://www.youtube.com/@gorgeousgeorgous"
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>About Upfixe | AI-Powered Google Ads Automation Platform</title>
        <meta name="description" content="Learn about Upfixe, founded by George Woodworth. We're transforming how businesses approach Google Ads through intelligent automation and real-world expertise." />
        <meta name="keywords" content="about Upfixe, Google Ads platform, automation solution, George Woodworth" />
        <meta property="og:title" content="About Upfixe | AI-Powered Google Ads Automation Platform" />
        <meta property="og:description" content="Learn about Upfixe, founded by George Woodworth. We're transforming how businesses approach Google Ads through intelligent automation." />
        <link rel="canonical" href="https://upfixe.com/about" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 py-12 pt-32 flex-grow">
        <h1 className="text-3xl font-bold mb-8">About Upfixe Ads</h1>
        
        <div className="prose max-w-none">
          <p className="lead text-lg mb-6">
            Upfixe Ads is transforming how businesses approach Google Ads by combining expert knowledge with powerful automation.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Our Story</h2>
          <p className="text-muted-foreground mb-4">
            Upfixe Ads began with a vision to make Google Ads management more accessible through automation. After years of seeing businesses struggle with complex advertising platforms, I built a solution that makes sophisticated advertising accessible to everyone, regardless of their technical expertise or budget.
          </p>
          <p className="text-muted-foreground">
            The platform combines deep automation expertise with real-world Google Ads knowledge and insight into advertiser pain points. The result is a tool that actually works in the real world.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Our Mission</h2>
          <p className="text-muted-foreground">
            I believe that effective digital advertising shouldn't be limited to those with large budgets or specialized knowledge. My mission is to empower businesses of all sizes to achieve exceptional results through intelligent, automated advertising solutions.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Meet The Founder</h2>
          <div className="max-w-xl mt-8">
            <div className="flex flex-col p-6 rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md">
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={founder.avatar} alt={founder.name} />
                  <AvatarFallback className="text-lg">{founder.initials}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-xl font-bold">{founder.name}</h3>
                  <p className="text-sm text-primary font-medium">{founder.role}</p>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-4">{founder.bio}</p>
              
              <div className="mt-auto pt-4 flex flex-wrap gap-3">
                <a 
                  href={founder.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:underline"
                >
                  LinkedIn <ExternalLink className="ml-1 h-3 w-3" />
                </a>
                <a 
                  href={founder.youtube} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:underline"
                >
                  YouTube <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default About;
